import { BaseType, BuildComponentTypeDefinition, OutdoorCategory, UtilityCategory } from '../types/Enums';

/** Per-component category value for the base types that have one (Utility/Outdoor). */
export type ComponentCategory = UtilityCategory | OutdoorCategory;

export type CategoryField = 'utilityCategory' | 'outdoorCategory';

export type TypeEntry = [string, BuildComponentTypeDefinition];

/**
 * The component field holding the category for this base type,
 * or null for base types without a per-component category.
 */
export function getCategoryField(baseType: BaseType): CategoryField | null {
    if (baseType === BaseType.Utility) return 'utilityCategory';
    if (baseType === BaseType.Outdoor) return 'outdoorCategory';
    return null;
}

/** All selectable categories for a base type (empty for base types without categories). */
export function getCategoriesForBaseType(baseType: BaseType): ComponentCategory[] {
    if (baseType === BaseType.Utility) return Object.values(UtilityCategory);
    if (baseType === BaseType.Outdoor) return Object.values(OutdoorCategory);
    return [];
}

/** The category currently set on a component instance, or null. */
export function getComponentCategory(component: {
    type: BaseType;
    utilityCategory?: UtilityCategory;
    outdoorCategory?: OutdoorCategory;
}): ComponentCategory | null {
    if (component.type === BaseType.Utility) return component.utilityCategory ?? null;
    if (component.type === BaseType.Outdoor) return component.outdoorCategory ?? null;
    return null;
}

/** Sort type entries alphabetically by Swedish translation. */
export function sortTypeEntries(entries: TypeEntry[]): TypeEntry[] {
    return [...entries].sort((a, b) => a[1].translations.sv.localeCompare(b[1].translations.sv, 'sv'));
}

/**
 * The type definitions allowed for a base type, narrowed to a category when one
 * is given and the base type has categories (Utility/Outdoor). Sorted by
 * Swedish translation.
 */
export function getTypesForCategory(
    types: Record<string, BuildComponentTypeDefinition>,
    baseType: BaseType,
    category: string | null
): TypeEntry[] {
    return sortTypeEntries(
        Object.entries(types).filter(([, def]) => {
            if (def.baseType !== baseType) return false;
            if (category && getCategoryField(baseType)) return def.category === category;
            return true;
        })
    );
}
